'use client';

import React, { useState } from 'react';
import { WhatsAppInlineButton } from '@/components/WhatsAppButton';
import SuccessModal from './SuccessModal';
import { trackHomepageContactFormSubmit, trackPhoneCall } from '@/lib/conversions';
import MultiSelectDienst from './MultiSelectDienst';

interface FormData {
  naam: string;
  telefoon: string;
  email: string;
  diensten: string[];
  merkModel: string;
  bericht: string;
}

export default function HomeContactForm() {
  const [formData, setFormData] = useState<FormData>({
    naam: '', telefoon: '', email: '',
    diensten: [], merkModel: '', bericht: '',
  });
  const [sending, setSending] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [sendError, setSendError] = useState('');
  const [emailError, setEmailError] = useState('');

  const update = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'email') setEmailError('');
  };

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) {
      setEmailError('E-mailadres is verplicht.');
      return;
    }
    if (!validateEmail(formData.email)) {
      setEmailError('Voer een geldig e-mailadres in.');
      return;
    }
    setSending(true);
    setSendError('');
    try {
      const res = await fetch('/api/send-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          dienst: formData.diensten.join(', '),
          diensten: formData.diensten,
          bron: 'Homepage',
        }),
      });
      if (!res.ok) {
        setSendError('Er is een fout opgetreden. Probeer opnieuw of bel ons.');
      } else {
        trackHomepageContactFormSubmit(formData.diensten[0] || '', formData.merkModel || formData.diensten[0] || '');
        setShowModal(true);
      }
    } catch {
      setSendError('Er is een fout opgetreden. Probeer opnieuw of bel ons.');
    } finally {
      setSending(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setFormData({ naam: '', telefoon: '', email: '', diensten: [], merkModel: '', bericht: '' });
  };

  return (
    <>
      <SuccessModal visible={showModal} naam={formData.naam} onClose={handleModalClose} />
      <div className="bg-secondary/30 rounded-2xl p-4 sm:p-8 border border-border">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label className="block text-sm font-bold text-foreground mb-2">Naam *</label>
            <input
              type="text"
              required
              value={formData.naam}
              onChange={(e) => update('naam', e.target.value)}
              className="w-full border border-border rounded-xl px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white"
              placeholder="Uw naam"
              autoComplete="name"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-foreground mb-2">Telefoon *</label>
            <input
              type="tel"
              required
              value={formData.telefoon}
              onChange={(e) => update('telefoon', e.target.value)}
              className="w-full border border-border rounded-xl px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white"
              placeholder="Uw telefoonnummer"
              inputMode="tel"
              autoComplete="tel"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-foreground mb-2">E-mail *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => update('email', e.target.value)}
              className="w-full border border-border rounded-xl px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white"
              placeholder="Uw e-mailadres"
              inputMode="email"
              autoComplete="email"
            />
            {emailError && <p className="text-red-600 text-sm mt-1">{emailError}</p>}
          </div>
          <div>
            <label className="block text-sm font-bold text-foreground mb-2">Merk &amp; Model</label>
            <input
              type="text"
              value={formData.merkModel}
              onChange={(e) => update('merkModel', e.target.value)}
              className="w-full border border-border rounded-xl px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white"
              placeholder="bv. BMW 320d 2018"
              autoComplete="off"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-bold text-foreground mb-2">Probleem / Dienst</label>
            <MultiSelectDienst
              value={formData.diensten}
              onChange={(selected) => setFormData((prev) => ({ ...prev, diensten: selected }))}
              inputClassName="py-4 text-base"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-bold text-foreground mb-2">Bericht</label>
            <textarea
              value={formData.bericht}
              onChange={(e) => update('bericht', e.target.value)}
              rows={4}
              className="w-full border border-border rounded-xl px-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white resize-none"
              placeholder="Beschrijf uw probleem of stel uw vraag"
            />
          </div>
          {sendError && (
            <div className="sm:col-span-2">
              <p className="text-sm text-red-600">{sendError}</p>
            </div>
          )}
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={sending}
              className="w-full bg-primary text-white py-5 rounded-full font-extrabold text-base hover:opacity-90 transition-opacity disabled:opacity-60 mb-3 min-h-[56px]"
            >
              {sending ? 'Verzenden...' : '✉️ Verstuur Offerte Aanvraag'}
            </button>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground font-medium">of</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <a
              href="tel:0498545474"
              onClick={() => trackPhoneCall('homepage-contact-form')}
              className="w-full flex items-center justify-center gap-2 border-2 border-primary text-primary py-4 rounded-full font-bold text-base hover:bg-primary/5 transition-colors mb-3 min-h-[56px]"
            >
              📞 Bel Direct: 0498 54 54 74
            </a>
            <WhatsAppInlineButton
              label="Stuur een bericht"
              message="Hallo, ik wil graag een offerte aanvragen."
              className="w-full justify-center py-4 text-base min-h-[56px]"
              location="homepage-contact-form"
            />
          </div>
        </form>
      </div>
    </>
  );
}
